import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { toast } from 'sonner';
import { SendToUsersDto, SendToGroupDto, NotificationType, NotificationPriority } from '../../../api/notificationApi';

interface CreateScheduledNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: any[];
  groups: any[];
  users: any[];
  onSendToUsers: (dto: SendToUsersDto) => Promise<any>;
  onSendToGroups: (dto: SendToGroupDto) => Promise<any>;
}

export default function CreateScheduledNotificationModal({ 
  isOpen, 
  onClose, 
  templates, 
  groups, 
  users,
  onSendToUsers,
  onSendToGroups
}: CreateScheduledNotificationModalProps) {
  const [formData, setFormData] = useState({
    subject: '',
    body: '',
    type: 'SYSTEM' as NotificationType,
    priority: 'NORMAL' as NotificationPriority,
    scheduledFor: '',
    templateId: '',
    targetType: 'users', // 'users' or 'group'
    selectedUsers: [] as string[],
    selectedGroups: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.body.trim() || !formData.scheduledFor) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.targetType === 'users' && formData.selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    if (formData.targetType === 'group' && formData.selectedGroups.length === 0) {
      toast.error('Please select at least one group');
      return;
    }

    setIsSubmitting(true);
    try {
      const scheduledDate = new Date(formData.scheduledFor);
      
      if (scheduledDate <= new Date()) {
        toast.error('Scheduled time must be in the future');
        return;
      }
      
      if (formData.targetType === 'users') {
        const dto: SendToUsersDto = {
          userIds: formData.selectedUsers,
          subject: formData.subject,
          body: formData.body,
          type: formData.type,
          priority: formData.priority,
          templateId: formData.templateId && formData.templateId !== 'no-template' ? formData.templateId : undefined,
          scheduledFor: scheduledDate,
        };
        await onSendToUsers(dto);
        handleClose();
      } else {
        const dto: SendToGroupDto = {
          groupId: formData.selectedGroups[0], // Use first selected group
          subject: formData.subject,
          body: formData.body,
          type: formData.type,
          priority: formData.priority,
          templateId: formData.templateId && formData.templateId !== 'no-template' ? formData.templateId : undefined,
          scheduledFor: scheduledDate,
        };
        await onSendToGroups(dto);
        handleClose();
      }
    } catch (error) {
      console.error('Error creating scheduled notification:', error);
      toast.error('Failed to create scheduled notification');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      subject: '',
      body: '',
      type: NotificationType.SYSTEM,
      priority: NotificationPriority.NORMAL,
      scheduledFor: '',
      templateId: '',
      targetType: 'users',
      selectedUsers: [],
      selectedGroups: [],
    });
    onClose();
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTemplateSelect = (templateId: string) => {
    if (templateId === 'no-template') {
      setFormData(prev => ({
        ...prev,
        subject: '',
        body: '',
        type: NotificationType.SYSTEM,
        templateId: '',
      }));
    } else if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setFormData(prev => ({
          ...prev,
          subject: template.subject,
          body: template.bodyTemplate,
          type: template.type,
          templateId,
        }));
      }
    }
  };

  const handleUserSelection = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId],
    }));
  };

  const handleGroupSelection = (groupId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedGroups: prev.selectedGroups.includes(groupId)
        ? prev.selectedGroups.filter(id => id !== groupId)
        : [...prev.selectedGroups, groupId],
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Scheduled Notification</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SYSTEM">System</SelectItem>
                  <SelectItem value="COURSE">Course</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="WELCOME">Welcome</SelectItem>
                  <SelectItem value="REMINDER">Reminder</SelectItem>
                  <SelectItem value="ALERT">Alert</SelectItem>
                  <SelectItem value="CUSTOM">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="template">Use Template (Optional)</Label>
            <Select value={formData.templateId} onValueChange={handleTemplateSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-template">No Template</SelectItem>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="Enter notification subject"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Message Body *</Label>
            <Textarea
              id="body"
              value={formData.body}
              onChange={(e) => handleInputChange('body', e.target.value)}
              placeholder="Enter notification message"
              rows={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="scheduledFor">Schedule For *</Label>
            <Input
              id="scheduledFor"
              type="datetime-local"
              value={formData.scheduledFor}
              onChange={(e) => handleInputChange('scheduledFor', e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Target Type</Label>
            <div className="flex space-x-4">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="targetUsers"
                  name="targetType"
                  value="users"
                  checked={formData.targetType === 'users'}
                  onChange={(e) => handleInputChange('targetType', e.target.value)}
                />
                <Label htmlFor="targetUsers">Specific Users</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="targetGroup"
                  name="targetType"
                  value="group"
                  checked={formData.targetType === 'group'}
                  onChange={(e) => handleInputChange('targetType', e.target.value)}
                />
                <Label htmlFor="targetGroup">Groups</Label>
              </div>
            </div>
          </div>

          {formData.targetType === 'users' && (
            <div className="space-y-2">
              <Label>Select Users</Label>
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-2">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`user-${user.id}`}
                      checked={formData.selectedUsers.includes(user.id)}
                      onCheckedChange={() => handleUserSelection(user.id)}
                    />
                    <Label htmlFor={`user-${user.id}`} className="text-sm">
                      {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User'} ({user.indianCdc || 'N/A'})
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {formData.targetType === 'group' && (
            <div className="space-y-2">
              <Label>Select Groups</Label>
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-2">
                {groups.map((group) => (
                  <div key={group.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`group-${group.id}`}
                      checked={formData.selectedGroups.includes(group.id)}
                      onCheckedChange={() => handleGroupSelection(group.id)}
                    />
                    <Label htmlFor={`group-${group.id}`} className="text-sm">
                      {group.name} ({group.userCount} members)
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Schedule Notification'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 